import React from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link, useLocation, matchPath } from "react-router-dom";
import Loading from "Components/Loading/Loading";
import { FLYOUT_WIDTH, LEFT_BAR_WIDTH, TOP_BAR_HEIGHT } from "../constants";
import { Beta } from "./LeftMenuSubItem";
import { AnimatePresence } from "motion/react";
import { IProtectedRoute } from "Hooks/useProtectedRoutes";
import { RouteNames } from "Hooks/routeNames";
import { ApiReportResponse } from "@incendium/api";
import { IMenuData } from "./SideMenu";

const FlyoutMenu = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isPinned",
})<{ $isPinned: boolean }>(({ theme, $isPinned }) => ({
  position: $isPinned ? "static" : "fixed",
  left: $isPinned ? "100%" : LEFT_BAR_WIDTH,
  top: $isPinned ? 0 : TOP_BAR_HEIGHT,
  width: FLYOUT_WIDTH,
  height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
  backgroundColor: theme.palette.background.default,
  boxShadow: $isPinned
    ? "1px 0 0px 0px #d5daed"
    : "2px 0 4px -3px rgba(0, 0, 0, 0.15)",
  zIndex: $isPinned ? 1 : 1000,
  display: "flex",
  flexDirection: "column",
  borderRight: $isPinned ? "1px solid #e2e7fa" : "none",
  paddingRight: theme.spacing(0.5),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

const FloyoutContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(2.5, 1.5, 2.5, 2),

  overflowY: "auto",
  overflowX: "hidden",

  // Hide scrollbar by default
  "&::-webkit-scrollbar": {
    width: "8px",
  },

  "&::-webkit-scrollbar-track": {
    background: "transparent",
    margin: 0,
  },

  "&::-webkit-scrollbar-thumb": {
    background: "transparent",
    borderRadius: "4px",
    maxHeight: "50px",
  },

  // Show scrollbar on hover
  "&:hover::-webkit-scrollbar-thumb": {
    background: "#d1d8f6",
  },
}));

const PinButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1),
  position: "absolute",
  top: theme.spacing(1),
  zIndex: 1000,
  right: 0,
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

const FlyoutMenuItemButtonContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  "&:hover .delete-button": {
    opacity: 1,
  },
}));

const FlyoutMenuItemButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$isActive",
})<{ $isActive: boolean }>(({ theme, $isActive }) => ({
  width: "100%",
  display: "block",
  padding: theme.spacing(0.6, 1),
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  transition: "all 0.2s ease",
  textDecoration: "none",
  color: $isActive ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: 500,
  backgroundColor: $isActive ? "#d2d9f7" : "transparent",
  textAlign: "left",
  "& .MuiTypography-root": {
    textWrap: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "&:hover": {
    backgroundColor: "#DAE0F8",
    textDecoration: "none",
    color: theme.palette.primary.main,
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(0.5),
  top: "50%",
  transform: "translateY(-50%)",
  opacity: 0,
  transition: "opacity 0.2s ease",
  padding: theme.spacing(0.5),
  background: "white",
  "&:hover": {
    background: "white",
    color: theme.palette.error.main,
  },
}));

const FlyoutMenuItemContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  "&:hover .delete-button": {
    opacity: 1,
  },
}));

const FlyoutMenuItem = styled(Link, {
  shouldForwardProp: (prop) => prop !== "$isActive",
})<{ $isActive: boolean }>(({ theme, $isActive }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.6, 1),
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  transition: "all 0.2s ease",
  textDecoration: "none",
  color: $isActive ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: 500,
  backgroundColor: $isActive ? "#e5ebfe" : "transparent",
  "&:hover": {
    backgroundColor: "#dee5fb",
    textDecoration: "none",
    color: theme.palette.primary.main,
  },
  "&:visited": {
    color: $isActive ? theme.palette.primary.main : theme.palette.text.primary,
  },
}));

const FlyoutMenuTitle = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  color: theme.palette.text.secondary,
  padding: theme.spacing(1),
  fontWeight: 600,
  textTransform: "uppercase",
}));

const FlyoutMenuGroupHeading = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1, 1, 0.5),
}));

const FlyoutMenuGroupTitle = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 700,
  textTransform: "uppercase",
  color: theme.palette.primary.main,
}));

const FlyoutMenuDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(2, 1, 1.5),
}));

const FlyoutMenuSection = styled(Box)(({ theme }) => ({
  marginBottom: "20px",
  "&:last-child": {
    marginBottom: 0,
  },
}));

const FlyoutMenuText = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 500,
  color: "inherit",
}));

interface FlyoutMenuProps {
  isSecondaryOpen: boolean;
  isSecondaryAvailable: boolean;
  items: Array<IMenuData>;
  isPinned: boolean;
  onPinToggle: () => void;
  loading?: boolean;
  favReports: ApiReportResponse[];
}

const FlyoutMenuComponent: React.FC<FlyoutMenuProps> = ({
  items,
  isPinned,
  onPinToggle,
  loading,
  isSecondaryOpen,
  isSecondaryAvailable,
  favReports,
}) => {
  const location = useLocation();

  const isItemActive = (item: { path: string; route: IProtectedRoute }) => {
    // issue with how we setup chart build and reports urls.
    if (location.pathname.includes("chart-library")) {
      return (
        item.route.path ===
        "/clients/:clientId/projects/:projectId/analyse/reports/chart-library"
      );
    }

    if (location.pathname.includes("/reports/")) {
      // check the report id matches
      const reportId = location.pathname.split("/").pop();
      if (reportId && favReports.some((r) => r.id === Number(reportId))) {
        return item.path === location.pathname;
      }
      return item.route.name === RouteNames.REPORTS;
    }

    // traffic has simular issue.
    if (location.pathname.includes("/train/traffic/")) {
      return item.route.name === RouteNames.TRAFFIC_CHANNELS;
    }

    // same for paid-media
    if (location.pathname.includes("/paid-media/")) {
      return item.path === location.pathname;
    }

    const match = matchPath(location.pathname, {
      path: item.route.path,
      exact: false,
      strict: false,
    });
    return match !== null;
  };

  return (
    <FlyoutMenu $isPinned={isPinned}>
      <FloyoutContainer>
        <AnimatePresence>
          {isSecondaryAvailable && <Box id="leftMenuOptions" mb={2} />}
          {!isSecondaryOpen && (
            <>
              {!isSecondaryAvailable && (
                <Tooltip arrow title="Pin to keep sidebar open">
                  <PinButton onClick={onPinToggle}>
                    {isPinned ? (
                      <PushPinIcon fontSize="small" />
                    ) : (
                      <PushPinOutlinedIcon fontSize="small" />
                    )}
                  </PinButton>
                </Tooltip>
              )}

              {items.map((section, index) => {
                if (section.variant === "divider") {
                  return <FlyoutMenuDivider key={index} />;
                }

                const renderItems = () =>
                  section.items.map((item, itemIndex) =>
                    item.action ? (
                      <FlyoutMenuItemButtonContainer key={itemIndex}>
                        <FlyoutMenuItemButton
                          onClick={item.action}
                          $isActive={false}
                        >
                          <FlyoutMenuText>{item.text}</FlyoutMenuText>
                        </FlyoutMenuItemButton>
                        {item.id !== undefined && item.deleteAction && (
                          <DeleteButton
                            className="delete-button"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              item.deleteAction!(item.id!);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </DeleteButton>
                        )}
                      </FlyoutMenuItemButtonContainer>
                    ) : (
                      <FlyoutMenuItemContainer key={itemIndex}>
                        <FlyoutMenuItem
                          to={item.path}
                          $isActive={isItemActive(item)}
                        >
                          <FlyoutMenuText>{item.text}</FlyoutMenuText>
                        </FlyoutMenuItem>
                        {item.id !== undefined && item.deleteAction && (
                          <DeleteButton
                            className="delete-button"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              item.deleteAction!(item.id!);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </DeleteButton>
                        )}
                      </FlyoutMenuItemContainer>
                    ),
                  );

                if (section.variant === "group") {
                  return (
                    <FlyoutMenuSection key={index}>
                      <FlyoutMenuGroupHeading>
                        <FlyoutMenuGroupTitle>
                          {section.title}
                        </FlyoutMenuGroupTitle>
                      </FlyoutMenuGroupHeading>
                      {renderItems()}
                    </FlyoutMenuSection>
                  );
                }

                return (
                  <FlyoutMenuSection key={index}>
                    <Stack
                      sx={{ width: "100%", position: "relative" }}
                      direction="row"
                      alignItems="space-between"
                      spacing={1}
                    >
                      <FlyoutMenuTitle>{section.title}</FlyoutMenuTitle>
                      {section.isBeta && <Beta>Beta</Beta>}
                    </Stack>
                    {renderItems()}
                  </FlyoutMenuSection>
                );
              })}
            </>
          )}
        </AnimatePresence>

        {loading && <Loading />}
      </FloyoutContainer>
    </FlyoutMenu>
  );
};

export default FlyoutMenuComponent;
